import { Spacing } from '@persona-id/design-tokens';
import { ZoomInIcon, ZoomOutIcon } from '@persona-id/design-tokens/icons';
import { keyBy } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import styled from 'styled-components';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import InquiryRunTree from 'dashboard/apps/InquiryTemplateEditor/InquiryRunTree';
import {
  buildInquiryRunTree,
  getRunStepTreeConfigs,
  inquiryEventsLoaded,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryRunTree/utils';
import { generatePages } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import { CombinedRunStepForTree } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  InquiryTemplateTreeProvider,
  useInquiryTemplateTreeStore,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/InquiryTemplateTreeProvider';
import { isInquiryRunEvent } from 'dashboard/apps/InquiryTemplateEditor/utils/inquiry-run';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import PanZoomBox, { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { TransformationMatrix2D } from 'dashboard/lib/utils/cssTransformationMatrix';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { RootState } from 'dashboard/redux/store';
import { loadInquiryRunData } from 'dashboard/redux/thunk-actions/inquiry-run';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Elevation } from 'design-system/variables/effect';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import useApiLoader from 'lib/hooks/useApiLoader';
import useDebounced from 'lib/hooks/useDebounced';
import useInquiryRunPermissionMap from 'lib/hooks/useInquiryRunPermissionMap';

const PANEL_WIDTH = 375;

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
`;

const EventsPanel = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  width: ${PANEL_WIDTH}px;
  height: 100%;
  background-color: ${({ theme }) => theme.color.background.primary};
  z-index: ${Z_INDICES.modalElevated};
`;

interface InquiryRunProps {
  currentEnvironment: Environment;
  inquiry: Inquiry;
  locale?: InquiryIso6391LanguageCode;
}

type InquiryRunComponentProps = PropsFromRedux & InquiryRunProps;

const ConnectedInquiryRun = ({
  currentEnvironment,
  inquiry,
  inquiryRunEvents,
  inquiryTemplateVersion,
  locale = 'en-US',
  stepsPreview,
  workflowVersionMap,
  runStepConfigs,
  ...props
}: InquiryRunComponentProps) => {
  const [runStepIndex, setRunStepIndex] = useState(null);
  // save initial load matrix for reset
  const [transformationMatrix, setTransformationMatrix] = useState<
    TransformationMatrix2D | undefined
  >(undefined);
  const [debouncedSetTransformationMatrix] = useDebounced(setTransformationMatrix, 500);
  const inquiryRunPermissionMap = useInquiryRunPermissionMap();
  const isITEInternal = useIsITEInternal();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const versionId = inquiry.relationships['inquiry-template-version'].data.id;
  const { loading } = useApiLoader({
    loader: async () =>
      await props.loadInquiryRunData({
        inquiryId: inquiry.id,
        inquiryTemplateVersionId: versionId,
        locale,
        environmentId: currentEnvironment.id,
        inquiryRunPermissionMap,
      }),
    deps: [],
    skip:
      inquiryTemplateVersion?.id === versionId && inquiryEventsLoaded(inquiryRunEvents, inquiry.id),
    onError: () => null,
    onNetworkError: () => null,
  });

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const runStepsTree = useMemo<CombinedRunStepForTree[]>(() => {
    if (
      inquiryTemplateVersion &&
      stepsPreview &&
      workflowVersionMap &&
      runStepConfigs?.length > 0
    ) {
      const { steps } = inquiryTemplateVersion.attributes;
      return buildInquiryRunTree({
        steps,
        stepsPreviewMap: keyBy('name', stepsPreview ?? []),
        previewPageMap: generatePages(stepsPreview),
        workflowVersionMap,
        runStepTreeConfigs: getRunStepTreeConfigs(
          runStepIndex != null ? runStepConfigs.slice(0, runStepIndex) : runStepConfigs,
        ),
      });
    }
  }, [inquiryTemplateVersion, runStepConfigs, runStepIndex, stepsPreview, workflowVersionMap]);

  const handleCardClick = useCallback(
    (eventTimestampToken: string) => {
      const runStepIndex = runStepConfigs.findIndex(
        ({ timestampToken }) => timestampToken === eventTimestampToken,
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setRunStepIndex(runStepIndex !== -1 ? runStepIndex + 1 : null);
    },
    [runStepConfigs],
  );

  const InquiryFlowEditorStore = useInquiryTemplateTreeStore({
    tree: runStepsTree,
  });

  if (loading || runStepsTree == null) {
    return (
      <ContentContainer>
        <LoadingIndicator />
      </ContentContainer>
    );
  }

  return (
    <InquiryTemplateTreeProvider value={InquiryFlowEditorStore}>
      <ContentContainer>
        <PanZoomBox
          showZoomControls={false}
          useKeydownShortcuts={isITEInternal}
          initialTransformationMatrix={transformationMatrix}
          overlayContent={
            <>
              <ZoomControls />
              <EventsPanel>
                <InquiryRunTree.Navigation
                  inquiry={inquiry}
                  events={inquiryRunEvents}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  inquiryTemplateVersion={inquiryTemplateVersion}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  transformationMatrix={transformationMatrix}
                  onRowSelected={handleCardClick}
                  onClearStepthrough={() => setRunStepIndex(null)}
                />
              </EventsPanel>
            </>
          }
          onTransformationMatrixChange={debouncedSetTransformationMatrix}
          zoomToFitPadding={{ top: 16, bottom: 16, right: PANEL_WIDTH }}
          withExternalZoomControls
          withDynamicUpdates
          zoomToFit
        >
          <InquiryRunTree
            tree={runStepsTree}
            workflowVersionMap={workflowVersionMap}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            theme={inquiryTemplateVersion.attributes.theme}
          />
        </PanZoomBox>
      </ContentContainer>
    </InquiryTemplateTreeProvider>
  );
};

const ZoomControlsContainer = styled.div`
  position: absolute;
  top: ${Spacing[4]};
  right: calc(${PANEL_WIDTH}px + ${Spacing[2]});
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: ${Spacing[9]};
  padding: ${Spacing[2]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  box-shadow: ${Elevation.High};
  z-index: ${Z_INDICES.modalElevated};
`;

const ZoomButton = styled(Button).attrs(() => ({
  width: 'icon',
  variant: 'basic',
}))`
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ZoomControls = () => {
  const {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    zoomControls: { zoomIn, zoomOut, percentage, minScale, maxScale },
  } = usePanZoomBoxControls();
  const atMaxZoomIn = percentage / 100 >= maxScale;
  const atMaxZoomOut = percentage / 100 <= minScale;
  return (
    <ZoomControlsContainer>
      <HStack spacing={Spacing[2]}>
        <ZoomButton
          id='inquiry-run__zoom-in-button'
          aria-label='Zoom in'
          aria-disabled={atMaxZoomIn}
          disabled={atMaxZoomIn}
          onClick={zoomIn}
        >
          <ZoomInIcon />
        </ZoomButton>
        <ZoomButton
          id='inquiry-run__zoom-out-button'
          aria-label='Zoom out'
          aria-disabled={atMaxZoomOut}
          disabled={atMaxZoomOut}
          onClick={zoomOut}
        >
          <ZoomOutIcon />
        </ZoomButton>
      </HStack>
    </ZoomControlsContainer>
  );
};

const mapStateToProps = (state: RootState) => ({
  inquiryRunEvents: state.inquiryRun.inquiryRunEvents?.filter(isInquiryRunEvent),
  inquiryTemplateVersion: state.inquiryRun.inquiryTemplateVersion,
  stepsPreview: state.inquiryRun.stepsPreview,
  workflowVersionMap: state.inquiryRun.workflowVersionMap,
  runStepConfigs: state.inquiryRun.runStepConfigs,
});

const mapDispatchToProps = {
  loadInquiryRunData,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConnectedInquiryRun);
